import { Component, Input } from '@angular/core';
import { agreementScheduledPayments } from '../agreement-scheduled-payments';

@Component({
  selector: 'app-agreement-template-australian',
  templateUrl: './agreement-template-australian.component.html',
  styleUrls: ['./agreement-template-australian.component.css']
})
export class AgreementTemplateAustralianComponent {
  @Input() entityData: any;
  @Input() studentAccountEntityData: any;
  @Input() studioInformationConfig: any;
  @Input() enrollmentForm: any;
  @Input() isTaxInclusive = false;
  @Input() displaySignatures = true;
  @Input() displaySigned = true;
  @Input() _enrollmentEntityData: any;

  get primaryContact(): any {
    const contacts = this.studentAccountEntityData?.field_contacts;
    if (!contacts) return null;
    if (Array.isArray(contacts)) {
      const active = contacts.filter(c => c.field_archived !== '1' && c.field_archived !== 1);
      return active[0] || contacts[0] || null;
    }
    return contacts;
  }

  calculateTotalTuition(): number {
    return this.entityData?.field_enrollment_total_price || 0;
  }

  getOriginalPackageCost(): number {
    if (this.entityData?.field_enrollment_lesson_price && this.entityData?.field_enrollment_lesson_count) {
      return this.entityData.field_enrollment_lesson_price * this.entityData.field_enrollment_lesson_count;
    }
    return 0;
  }

  getDiscount(): number {
    return this.entityData?.field_discount || 0;
  }

  getBalanceDue(): number {
    return this.entityData?.field_balance_due || this.calculateTotalTuition();
  }

  /**
   * The initial tuition payment: the scheduled payment flagged as the down
   * payment, else the enrollment-level field_payment_amount. Null when neither
   * is set, so the template leaves the blank for the studio to fill in.
   */
  get initialPaymentAmount(): number | null {
    const down = this.scheduledPaymentRows().find(r => r.down);
    if (down) return down.amount;
    return parseFloat(this.fieldValue(this.entityData, 'field_payment_amount')) || null;
  }

  /** The payments after the initial one, printed as the remaining-balance schedule. */
  get balancePaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    return this.scheduledPaymentRows().filter(r => !r.down);
  }

  /**
   * Scheduled payment rows normalized to {amount, date, down}, date ascending.
   *
   * Amounts are printed as stored, with no tax added: this agreement states
   * that GST is included in the total tuition, and the stored amounts sum to
   * it. The per-row field_tax_amount is not reliable for these studios (some
   * rows carry GST on top, others GST included), so it is not used here.
   */
  private scheduledPaymentRows(): { amount: number; date: string | null; down: boolean }[] {
    let raw = agreementScheduledPayments(this.enrollmentForm?.get?.('field_scheduled_payments')?.value);
    if (raw.length === 0) {
      raw = agreementScheduledPayments(this.entityData?.field_scheduled_payments);
    }

    return raw
      .map(r => ({
        amount: parseFloat(this.fieldValue(r, 'field_payment_amount')) || 0,
        date: this.fieldValue(r, 'field_payment_date') || null,
        down: ['1', 1, true].includes(this.fieldValue(r, 'field_down_payment'))
      }))
      .filter(r => r.amount > 0)
      .sort((x, y) => !x.date ? 1 : !y.date ? -1 : x.date.localeCompare(y.date));
  }

  private fieldValue(obj: any, fieldName: string): any {
    const v = obj?.[fieldName];
    if (Array.isArray(v)) return v[0]?.value ?? v[0];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  }

  get enrollmentTypeState() {
    const category = this.entityData?.field_category;
    const targetId = Array.isArray(category) ? category[0]?.target_id?.toString() : category?.target_id?.toString() || category?.toString();

    const map: { [key: string]: string } = {
      '50': 'original', '49': 'original',
      '51': 'extension', '543': 'extension', '546': 'extension',
      '65': 'renewal',
      '5': 'service', '66': 'service', '67': 'service', '68': 'service',
      '69': 'service', '542': 'service', '1373': 'service', '1395': 'service',
      '1421': 'original', '70': 'original'
    };

    const mapped = targetId ? map[targetId] : null;
    return {
      original: mapped === 'original',
      extension: mapped === 'extension',
      renewal: mapped === 'renewal',
      service: mapped === 'service' || mapped === null
    };
  }
}
